import { useSession } from "@d12labs/auth/client";
import { Button } from "@d12labs/ui/button";
import { ConfirmDialog } from "@d12labs/ui/confirm-dialog";
import { EmptyState } from "@d12labs/ui/empty-state";
import { Input } from "@d12labs/ui/input";
import { PageBody, PageHeader } from "@d12labs/ui/page-header";
import { Skeleton } from "@d12labs/ui/skeleton";
import { Textarea } from "@d12labs/ui/textarea";
import { cn } from "@d12labs/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
	Copy,
	MessageSquare,
	Pencil,
	Plus,
	RefreshCw,
	Search,
	Send,
	Sparkles,
	Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
// Shapes derive from the server's route defs (Phase 7) — no hand-typed
// Conversation/ConversationDetail.
import { aiChatApi, type Conversation, type ConversationDetail } from "../api";

type ChatMessage = ConversationDetail["messages"][number];
/** Display shape — satisfied by persisted rows and the transient local stubs alike. */
type Message = Pick<ChatMessage, "id" | "role" | "content">;

type SsePayload =
	| { type?: undefined; id: string; title: string; createdAt: string; updatedAt: string } // conversation event
	| { type: "delta"; text: string }
	| {
			type: "done";
			text: string;
			model: string;
			usage: { inputTokens: number; outputTokens: number };
			costUsd: number;
			latencyMs: number;
	  }
	| { type: "error"; message: string };

type ParsedSseEvent = { name: string; data: SsePayload };

function parseSseChunks(buffer: string): { events: ParsedSseEvent[]; rest: string } {
	const events: ParsedSseEvent[] = [];
	let rest = buffer;
	let sep = rest.indexOf("\n\n");
	while (sep !== -1) {
		const chunk = rest.slice(0, sep);
		rest = rest.slice(sep + 2);
		const lines = chunk.split("\n");
		const eventLine = lines.find((l) => l.startsWith("event:"));
		const dataLine = lines.find((l) => l.startsWith("data:"));
		if (dataLine) {
			const name = eventLine ? eventLine.slice(6).trim() : "message";
			try {
				const data = JSON.parse(dataLine.slice(5).trimStart()) as SsePayload;
				events.push({ name, data });
			} catch {
				// skip malformed chunk
			}
		}
		sep = rest.indexOf("\n\n");
	}
	return { events, rest };
}

/** Relative time label for conversation rows (e.g. "11m", "2h", "Yesterday"). */
function relativeLabel(iso: string | Date): string {
	const now = Date.now();
	const ms = now - new Date(iso).getTime();
	const mins = Math.floor(ms / 60_000);
	if (mins < 1) return "Just now";
	if (mins < 60) return `${mins}m`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h`;
	const days = Math.floor(hrs / 24);
	if (days === 1) return "Yesterday";
	if (days < 7) return `${days}d`;
	const weeks = Math.floor(days / 7);
	if (weeks < 5) return `${weeks}w`;
	return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** Initials from a name string for the user avatar chip. */
function initials(name: string): string {
	const parts = name.trim().split(/\s+/);
	if (parts.length === 0 || !parts[0]) return "?";
	if (parts.length === 1) return (parts[0][0] ?? "").toUpperCase();
	return ((parts[0][0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
}

export function ChatPage() {
	const params = useParams({ strict: false }) as { id?: string };
	const conversationId = params.id;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [draft, setDraft] = useState("");
	const [sending, setSending] = useState(false);
	const [profile, setProfile] = useState("default");
	const [liveMessages, setLiveMessages] = useState<Message[]>([]);
	const messageListRef = useRef<HTMLDivElement>(null);
	const { data: sessionData } = useSession();
	const userName = sessionData?.user?.name ?? "";

	const conversationsQuery = useQuery({
		queryKey: ["ai-chat", "conversations"],
		queryFn: () => aiChatApi.conversations(),
	});

	// Available model profiles for the send selector (provider/model mapping is
	// server-side; this just lists the names). Static at runtime → cache forever.
	const profilesQuery = useQuery({
		queryKey: ["ai-chat", "profiles"],
		queryFn: () => aiChatApi.profiles(),
		staleTime: Number.POSITIVE_INFINITY,
	});
	const profiles = profilesQuery.data?.profiles ?? ["default"];

	const detailQuery = useQuery({
		queryKey: ["ai-chat", "conversations", conversationId],
		queryFn: () => aiChatApi.conversation({ params: { id: conversationId ?? "" } }),
		enabled: !!conversationId,
	});

	// Reset transient send state when the user switches conversations. The
	// effect body doesn't *read* conversationId — it just needs to re-run on
	// change. Biome's exhaustive-deps rule flags this as "extra", but that's
	// exactly the pattern we want here.
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset-on-key pattern
	useEffect(() => {
		setLiveMessages([]);
		setSending(false);
	}, [conversationId]);

	// Auto-scroll the message list as deltas arrive.
	useEffect(() => {
		const el = messageListRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	});

	const messages: Message[] =
		liveMessages.length > 0 ? liveMessages : (detailQuery.data?.messages ?? []);

	const currentConversation = detailQuery.data?.conversation;

	const renameConversation = useMutation({
		mutationFn: ({ id, title }: { id: string; title: string }) =>
			aiChatApi.renameConversation({ params: { id }, input: { title } }),
		onSuccess: (updated) => {
			queryClient.invalidateQueries({ queryKey: ["ai-chat", "conversations"] });
			queryClient.invalidateQueries({ queryKey: ["ai-chat", "conversations", updated.id] });
			toast.success("Renamed");
		},
	});

	const deleteConversation = useMutation({
		mutationFn: (id: string) => aiChatApi.removeConversation({ params: { id } }),
		onSuccess: (_, deletedId) => {
			queryClient.invalidateQueries({ queryKey: ["ai-chat", "conversations"] });
			toast.success("Deleted");
			if (deletedId === conversationId) {
				navigate({ to: "/ai-chat" });
			}
		},
	});

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const content = draft.trim();
		if (content.length === 0 || sending) return;

		setDraft("");
		setSending(true);

		// Seed the live message list so the user sees their question + an empty
		// assistant stub immediately. We start from the persisted messages so
		// switching conversations mid-send still shows the right history.
		const seed: Message[] = [
			...(detailQuery.data?.messages ?? []),
			{ id: `local-user-${Date.now()}`, role: "user", content },
			{ id: `local-assistant-${Date.now()}`, role: "assistant", content: "" },
		];
		setLiveMessages(seed);
		const assistantIdx = seed.length - 1;

		try {
			// Stays a hand-rolled fetch: this POST answers with an SSE stream that we
			// read incrementally — typedRoute is JSON-only.
			const res = await fetch("/api/plugins/ai-chat/messages", {
				method: "POST",
				credentials: "same-origin",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					conversationId: conversationId ?? null,
					content,
					profile,
				}),
			});
			if (!res.ok || !res.body) {
				const err = (await res.json().catch(() => ({ error: res.statusText }))) as {
					error?: string;
				};
				throw new Error(err.error ?? `Request failed (${res.status})`);
			}

			const reader = res.body.getReader();
			const decoder = new TextDecoder();
			let buffer = "";
			let createdConversationId: string | null = null;

			while (true) {
				const { done, value } = await reader.read();
				if (done) {
					buffer += decoder.decode();
					break;
				}
				buffer += decoder.decode(value, { stream: true });
				const { events, rest } = parseSseChunks(buffer);
				buffer = rest;

				for (const { name, data } of events) {
					if (name === "conversation" && !("type" in data)) {
						// First event — gives us the conversation id (esp. important
						// for newly-created ones). Cache the detail so the URL nav
						// doesn't trigger a flash of "loading". The derived row shape
						// carries userId (the signed-in user owns it) and Date
						// timestamps — the SSE payload sends ISO strings.
						createdConversationId = data.id;
						const detail: ConversationDetail = {
							conversation: {
								id: data.id,
								userId: sessionData?.user?.id ?? "",
								title: data.title,
								createdAt: new Date(data.createdAt),
								updatedAt: new Date(data.updatedAt),
							},
							messages: [],
						};
						queryClient.setQueryData(["ai-chat", "conversations", data.id], detail);
					} else if ("type" in data && data.type === "delta") {
						const text = data.text;
						setLiveMessages((prev) => {
							const copy = prev.slice();
							const cur = copy[assistantIdx];
							if (cur) copy[assistantIdx] = { ...cur, content: cur.content + text };
							return copy;
						});
					} else if ("type" in data && data.type === "done") {
						setLiveMessages((prev) => {
							const copy = prev.slice();
							copy[assistantIdx] = {
								...(copy[assistantIdx] ?? { id: "", role: "assistant", content: "" }),
								role: "assistant",
								content: data.text,
							};
							return copy;
						});
					} else if ("type" in data && data.type === "error") {
						throw new Error(data.message);
					}
				}
			}

			// Stream complete. Sync server state, then drop the live overlay so
			// subsequent edits flow through the canonical query data.
			const targetId = createdConversationId ?? conversationId;
			if (targetId) {
				await queryClient.invalidateQueries({
					queryKey: ["ai-chat", "conversations", targetId],
				});
			}
			await queryClient.invalidateQueries({ queryKey: ["ai-chat", "conversations"] });

			if (createdConversationId && createdConversationId !== conversationId) {
				navigate({ to: "/ai-chat/$id", params: { id: createdConversationId } });
			}

			setLiveMessages([]);
		} catch (err) {
			toast.error((err as Error).message);
			// Roll back the live messages so the user can retry. Their original
			// message is still persisted server-side (we appended it before
			// streaming), so re-fetching the conversation gives the truth.
			setLiveMessages([]);
			setDraft(content);
			if (conversationId) {
				queryClient.invalidateQueries({
					queryKey: ["ai-chat", "conversations", conversationId],
				});
			}
		} finally {
			setSending(false);
		}
	};

	// Header title + subtitle for the active conversation.
	const headerTitle = currentConversation?.title ?? (conversationId ? "Loading…" : "AI Chat");
	const headerSubtitle = currentConversation
		? `Started ${relativeLabel(currentConversation.createdAt)} · ${messages.length} message${messages.length !== 1 ? "s" : ""}`
		: undefined;

	return (
		<>
			<PageHeader
				icon={MessageSquare}
				title={headerTitle}
				subtitle={headerSubtitle}
				actions={
					conversationId ? (
						<>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									if (!currentConversation) return;
									const newTitle = window.prompt("Rename conversation", currentConversation.title);
									if (newTitle?.trim()) {
										renameConversation.mutate({ id: conversationId, title: newTitle.trim() });
									}
								}}
							>
								<Pencil className="size-3.5" />
								Rename
							</Button>
							<ConfirmDialog
								title={`Delete "${currentConversation?.title ?? "this conversation"}"?`}
								description="This conversation and all its messages will be permanently removed."
								confirmLabel="Delete"
								destructive
								onConfirm={() => deleteConversation.mutate(conversationId)}
							>
								<Button variant="ghost" size="sm">
									<Trash2 className="size-3.5" />
									Delete
								</Button>
							</ConfirmDialog>
						</>
					) : undefined
				}
			/>
			<PageBody className="p-0">
				<div className="flex h-full min-h-0 flex-row">
					{/* Conversation rail */}
					<ConversationSidebar
						conversations={conversationsQuery.data ?? []}
						isLoading={conversationsQuery.isLoading}
						activeId={conversationId ?? null}
						onRename={(id, title) => renameConversation.mutate({ id, title })}
						onDelete={(id) => deleteConversation.mutate(id)}
					/>

					{/* Main chat pane */}
					<div className="flex min-h-0 flex-1 flex-col border-l border-hairline">
						{/* Message list — owns its own scroll */}
						<div
							ref={messageListRef}
							className="flex flex-1 flex-col gap-5 overflow-y-auto px-6 py-5"
						>
							{!conversationId && messages.length === 0 ? (
								<EmptyState
									icon={Sparkles}
									title="Start a conversation"
									body="Ask Claude anything — your chats are saved and searchable."
									className="m-auto"
								/>
							) : detailQuery.isLoading && messages.length === 0 ? (
								<div className="flex flex-col gap-5">
									<Skeleton h={48} className="w-2/3" />
									<Skeleton h={80} className="w-3/4" />
									<Skeleton h={48} className="w-1/2" />
								</div>
							) : messages.length === 0 ? (
								<EmptyState
									icon={Sparkles}
									title="Empty conversation"
									body="Say hi to Claude — type your first message below."
									small
									className="m-auto"
								/>
							) : (
								messages.map((m) => (
									<MessageBubble key={m.id} message={m} sending={sending} userName={userName} />
								))
							)}
						</div>

						{/* Composer — pinned to bottom */}
						<div className="flex-none border-t border-hairline bg-canvas p-4">
							<form onSubmit={handleSubmit}>
								<div className="rounded-lg border border-hairline bg-surface-1 transition-colors focus-within:border-primary/50">
									<Textarea
										value={draft}
										onChange={(e) => setDraft(e.target.value)}
										placeholder="Reply to Claude… (⌘+Enter to send)"
										rows={2}
										disabled={sending}
										className="block w-full resize-none border-0 bg-transparent px-3.5 py-2.5 text-body-sm text-ink shadow-none outline-none placeholder:text-ink-tertiary focus-visible:ring-0"
										onKeyDown={(e) => {
											if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
												e.preventDefault();
												handleSubmit(e as unknown as React.FormEvent);
											}
										}}
									/>
									<div className="flex items-center justify-between px-2 pb-2">
										<div className="flex items-center gap-2">
											{/* Model-profile selector — picks the provider/model behind the send. */}
											<select
												value={profile}
												onChange={(e) => setProfile(e.target.value)}
												disabled={sending}
												aria-label="Model profile"
												className="h-7 rounded-md border border-hairline bg-surface-1 px-1.5 text-meta text-ink-muted outline-none transition-colors focus-visible:border-primary/50 disabled:opacity-50"
											>
												{profiles.map((p) => (
													<option key={p} value={p}>
														{p}
													</option>
												))}
											</select>
											<span className="text-meta text-ink-subtle">⌘+Enter to send</span>
										</div>
										<Button type="submit" size="sm" disabled={sending || draft.trim().length === 0}>
											{sending ? (
												<>
													<RefreshCw className="size-3.5 animate-spin" />
													Sending…
												</>
											) : (
												<>
													<Send className="size-3.5" />
													Send
												</>
											)}
										</Button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</PageBody>
		</>
	);
}

function MessageBubble({
	message: m,
	sending,
	userName,
}: {
	message: Message;
	sending: boolean;
	userName: string;
}) {
	const isStreaming = m.role === "assistant" && m.content.length === 0 && sending;

	const handleCopy = () => {
		navigator.clipboard.writeText(m.content).then(() => toast.success("Copied"));
	};

	if (m.role === "user") {
		return (
			<div className="flex gap-3">
				{/* User initials chip */}
				<span className="flex size-7 flex-none select-none items-center justify-center rounded-full bg-surface-3 text-meta font-semibold text-ink-muted">
					{initials(userName)}
				</span>
				<div className="max-w-3xl flex-1">
					<div className="mb-1 text-meta text-ink-subtle">You</div>
					<div className="text-body-sm text-ink-muted whitespace-pre-wrap">{m.content}</div>
				</div>
			</div>
		);
	}

	// assistant
	return (
		<div className="flex gap-3">
			{/* Claude sparkle chip */}
			<span
				className={cn(
					"flex size-7 flex-none items-center justify-center rounded-full border border-primary/25 bg-primary/10 text-primary",
					isStreaming && "animate-pulse",
				)}
			>
				<Sparkles className="size-3.5" />
			</span>
			<div className="max-w-3xl flex-1">
				<div className="mb-1 text-meta text-ink-subtle">
					{isStreaming ? "Claude · streaming…" : "Claude"}
				</div>
				{isStreaming ? (
					<div className="text-body-sm text-ink-subtle italic">Thinking…</div>
				) : (
					<>
						<div className="markdown-body text-body-sm text-ink-muted">
							<ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
								{m.content}
							</ReactMarkdown>
						</div>
						{m.content.length > 0 && (
							<div className="mt-2 flex items-center gap-3 text-ink-subtle">
								<button
									type="button"
									onClick={handleCopy}
									className="flex items-center gap-1.5 text-meta transition-colors hover:text-ink"
								>
									<Copy className="size-3" />
									Copy
								</button>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

function ConversationSidebar({
	conversations,
	isLoading,
	activeId,
	onRename,
	onDelete,
}: {
	conversations: Conversation[];
	isLoading: boolean;
	activeId: string | null;
	onRename: (id: string, title: string) => void;
	onDelete: (id: string) => void;
}) {
	const [renamingId, setRenamingId] = useState<string | null>(null);
	const [renameDraft, setRenameDraft] = useState("");
	const [search, setSearch] = useState("");

	const startRename = (c: Conversation) => {
		setRenamingId(c.id);
		setRenameDraft(c.title);
	};

	const commitRename = (id: string) => {
		const title = renameDraft.trim();
		if (title.length === 0) {
			toast.error("Title can't be empty");
			return;
		}
		onRename(id, title);
		setRenamingId(null);
	};

	const filtered = search.trim()
		? conversations.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()))
		: conversations;

	return (
		<aside className="flex w-[240px] flex-none flex-col border-r border-hairline bg-canvas">
			{/* New conversation button */}
			<div className="flex-none px-2 pt-2 pb-1">
				<Link to="/ai-chat" className="block">
					<Button variant="secondary" size="sm" className="w-full justify-center gap-1.5">
						<Plus className="size-3.5" />
						New conversation
					</Button>
				</Link>
			</div>

			{/* Search */}
			<div className="flex-none px-2 pb-2">
				<div className="flex h-7 items-center gap-2 rounded-md border border-hairline bg-surface-1 px-2.5 transition-colors focus-within:border-primary/50">
					<Search className="size-3 flex-none text-ink-tertiary" />
					<input
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search…"
						className="min-w-0 flex-1 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-tertiary"
					/>
				</div>
			</div>

			{/* Conversation list */}
			<div className="flex-1 overflow-y-auto px-1 pb-2">
				{isLoading ? (
					<div className="flex flex-col gap-1 px-1 pt-1">
						<Skeleton h={40} />
						<Skeleton h={40} />
						<Skeleton h={40} />
					</div>
				) : filtered.length === 0 ? (
					<p className="px-2 pt-2 text-caption text-ink-subtle">
						{search ? "No matches." : "No conversations yet."}
					</p>
				) : (
					<ul className="flex flex-col">
						{filtered.map((c) => {
							const isActive = c.id === activeId;
							const isRenaming = c.id === renamingId;
							return (
								<li key={c.id}>
									{isRenaming ? (
										<div className="flex items-center gap-1 rounded-md p-1">
											<Input
												value={renameDraft}
												onChange={(e) => setRenameDraft(e.target.value)}
												onKeyDown={(e) => {
													if (e.key === "Enter") commitRename(c.id);
													if (e.key === "Escape") setRenamingId(null);
												}}
												className="h-7 text-body-sm"
												autoFocus
											/>
											<Button
												type="button"
												variant="ghost"
												size="sm"
												className="h-7 px-2 text-caption"
												onClick={() => commitRename(c.id)}
											>
												Save
											</Button>
										</div>
									) : (
										<div
											className={cn(
												"group relative flex cursor-pointer flex-col rounded-md p-2 transition-colors",
												isActive ? "bg-surface-2" : "hover:bg-surface-1",
											)}
										>
											<Link to="/ai-chat/$id" params={{ id: c.id }} className="flex flex-col">
												<span
													className={cn(
														"truncate text-body-sm",
														isActive ? "font-medium text-ink" : "text-ink-muted",
													)}
												>
													{c.title}
												</span>
												<span className="text-meta text-ink-subtle">
													{relativeLabel(c.updatedAt)}
												</span>
											</Link>
											{/* Row actions — visible on hover */}
											<div className="absolute right-1 top-1/2 hidden -translate-y-1/2 items-center gap-0.5 group-hover:flex">
												<button
													type="button"
													onClick={(e) => {
														e.preventDefault();
														startRename(c);
													}}
													className="flex size-6 items-center justify-center rounded text-ink-subtle transition-colors hover:bg-surface-3 hover:text-ink"
													title="Rename"
												>
													<Pencil className="size-3" />
												</button>
												<ConfirmDialog
													title={`Delete "${c.title}"?`}
													description="This conversation and all its messages will be permanently removed."
													confirmLabel="Delete"
													destructive
													onConfirm={() => onDelete(c.id)}
												>
													<button
														type="button"
														className="flex size-6 items-center justify-center rounded text-ink-subtle transition-colors hover:bg-surface-3 hover:text-destructive"
														title="Delete"
													>
														<Trash2 className="size-3" />
													</button>
												</ConfirmDialog>
											</div>
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</aside>
	);
}
